<template>
  <section
    class="try-download-animation"
    :class="{ 'try-download-animation--failed': isFailed }"
    role="status"
    aria-live="polite"
  >
    <div class="try-download-animation-inner">
      <div v-if="platformIconUrl && !isFailed && showPlatform" class="try-download-animation-platform">
        <span class="try-download-animation-platform-icon-wrap">
          <img :src="platformIconUrl" :alt="resolvedPlatformLabel" class="try-download-animation-platform-icon" />
        </span>
        <p class="try-download-animation-platform-label">
          {{ resolvedPlatformLabel }}
        </p>
      </div>

      <div v-if="isFailed" class="try-download-animation-error-icon" aria-hidden="true">
        <span class="try-download-animation-error-ring"></span>
        <span class="try-download-animation-error-core">
          <i class="ti-alert"></i>
        </span>
      </div>

      <div v-else class="try-download-animation-spinner">
        <span class="spinner-aura" aria-hidden="true"></span>
        <span class="spinner-orbit spinner-orbit--one" aria-hidden="true"></span>
        <span class="spinner-orbit spinner-orbit--two" aria-hidden="true"></span>
        <span class="spinner-orbit spinner-orbit--three" aria-hidden="true"></span>
        <span class="spinner-ring spinner-ring--outer"></span>
        <span class="spinner-ring spinner-ring--inner"></span>
        <span class="spinner-core">
          <img src="/favicon.svg" alt="" class="spinner-core-logo" />
        </span>
      </div>

      <p v-if="showEyebrow" class="try-download-animation-eyebrow">{{ loaderEyebrow }}</p>
      <h3 v-if="showTitle" :class="{ 'try-download-animation-title--failed': isFailed }">{{ resolvedTitle }}</h3>
      <p v-if="resolvedSubtitle" class="try-download-animation-subtitle">{{ resolvedSubtitle }}</p>
    </div>
  </section>
</template>

<script>
export default {
  name: "TryDownloadAnimation",
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    platformIconUrl: {
      type: String,
      default: "",
    },
    platformLabel: {
      type: String,
      default: "",
    },
  },
  computed: {
    resolvedTitle() {
      return this.title || this.$t("tryClips.processingVideoTitle");
    },
    resolvedSubtitle() {
      return this.subtitle;
    },
    resolvedPlatformLabel() {
      return this.platformLabel || this.$t("tryClips.downloadAnimation.platformDetected");
    },
    showTitle() {
      const normalized = String(this.resolvedTitle || "").trim().toLowerCase();
      return normalized !== "descargando" && normalized !== "downloading";
    },
    showPlatform() {
      if (this.isFailed) return false;
      const normalized = String(this.resolvedTitle || "").toLowerCase();
      return !normalized.includes("render");
    },
    showEyebrow() {
      if (this.isFailed) return true;
      const normalized = String(this.resolvedTitle || "").toLowerCase();
      if (normalized.includes("render")) return false;
      if (normalized.includes("descarg") || normalized.includes("download")) return false;
      return true;
    },
    isFailed() {
      const normalized = String(this.resolvedTitle || "").toLowerCase();
      return normalized.includes("fallido") || normalized.includes("failed") || normalized.includes("error");
    },
    loaderEyebrow() {
      if (this.isFailed) return this.$t("tryClips.downloadAnimation.failed");
      const normalized = String(this.resolvedTitle || "").toLowerCase();
      if (normalized.includes("render")) return this.$t("tryClips.loader.rendering");
      if (
        normalized.includes("descarg") ||
        normalized.includes("download") ||
        normalized.includes("cola") ||
        normalized.includes("queue")
      ) {
        return this.$t("tryClips.loader.downloading");
      }
      if (normalized.includes("validando") || normalized.includes("validating")) {
        return this.$t("tryClips.downloadAnimation.validating");
      }
      if (normalized.includes("comenzando") || normalized.includes("prepar") || normalized.includes("start")) {
        return this.$t("tryClips.downloadAnimation.preparing");
      }
      if (normalized.includes("url")) return this.$t("tryClips.downloadAnimation.confirmingUrl");
      return this.$t("tryClips.processing");
    },
  },
};
</script>

<style scoped>
.try-download-animation {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.try-download-animation--failed {
  background: transparent;
}

.try-download-animation-inner {
  position: relative;
  z-index: 1;
  padding: 8px 12px 4px;
  text-align: center;
}

.try-download-animation--failed .try-download-animation-inner {
  padding-bottom: 8px;
}

.try-download-animation-platform {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}

.try-download-animation-platform-icon-wrap {
  width: 78px;
  height: 78px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background:
    radial-gradient(circle at 50% 35%, rgba(34, 211, 238, 0.18), transparent 64%),
    rgba(15, 23, 42, 0.72);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 14px 28px rgba(34, 211, 238, 0.14);
}

.try-download-animation-platform-icon {
  width: 46px;
  height: 46px;
  object-fit: contain;
}

.try-download-animation-platform-label {
  margin: 0;
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.try-download-animation-spinner {
  position: relative;
  width: 88px;
  height: 88px;
  margin: 0 auto 20px;
}

.try-download-animation-error-icon {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto 18px;
}

.try-download-animation-error-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(248, 113, 113, 0.28);
  box-shadow: 0 0 24px rgba(248, 113, 113, 0.12);
}

.try-download-animation-error-core {
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(69, 10, 10, 0.35);
  border: 1px solid rgba(252, 165, 165, 0.28);
  color: #fca5a5;
  font-size: 1.15rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.try-download-animation-error-core i {
  line-height: 1;
}

.spinner-aura {
  position: absolute;
  inset: -18px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.16) 0%, rgba(168, 85, 247, 0.08) 42%, transparent 72%);
  animation: try-aura-pulse 2.2s ease-in-out infinite;
}

.spinner-orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: #67e8f9;
  box-shadow: 0 0 10px rgba(34, 211, 238, 0.8);
}

.spinner-orbit--one {
  animation: try-orbit-one 2.4s linear infinite;
}

.spinner-orbit--two {
  background: #c4b5fd;
  box-shadow: 0 0 10px rgba(168, 85, 247, 0.75);
  animation: try-orbit-two 3.1s linear infinite;
}

.spinner-orbit--three {
  width: 4px;
  height: 4px;
  background: #a5f3fc;
  box-shadow: 0 0 8px rgba(165, 243, 252, 0.7);
  animation: try-orbit-three 1.9s linear infinite;
}

.spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
}

.spinner-ring--outer {
  border-top-color: #22d3ee;
  border-right-color: rgba(34, 211, 238, 0.24);
  filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.35));
  animation: try-spin 1s linear infinite;
}

.spinner-ring--inner {
  inset: 12px;
  border-bottom-color: #a855f7;
  border-left-color: rgba(168, 85, 247, 0.24);
  filter: drop-shadow(0 0 6px rgba(168, 85, 247, 0.3));
  animation: try-spin-reverse 1.4s linear infinite;
}

.spinner-core {
  position: absolute;
  inset: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(196, 181, 253, 0.3);
  color: #67e8f9;
  font-size: 1rem;
  box-shadow:
    0 8px 20px rgba(34, 211, 238, 0.14),
    inset 0 0 16px rgba(34, 211, 238, 0.06);
}

.spinner-core-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.try-download-animation-eyebrow {
  margin: 0 0 8px;
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.try-download-animation--failed .try-download-animation-eyebrow {
  color: #fca5a5;
}

h3 {
  margin: 4px 0 8px;
  color: #f8fafc;
  font-size: clamp(1.2rem, 2.4vw, 1.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  background: linear-gradient(120deg, #f8fafc 0%, #ddd6fe 45%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.try-download-animation-title--failed {
  background: linear-gradient(120deg, #fecaca 0%, #f87171 55%, #fda4af 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.try-download-animation-subtitle {
  margin: 0 auto;
  max-width: 42ch;
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.55;
}

.try-download-animation--failed .try-download-animation-subtitle {
  margin-bottom: 0;
  color: #fda4af;
}

@keyframes try-aura-pulse {
  0%, 100% {
    opacity: 0.55;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

@keyframes try-orbit-one {
  from {
    transform: rotate(0deg) translateX(46px) rotate(0deg);
  }
  to {
    transform: rotate(360deg) translateX(46px) rotate(-360deg);
  }
}

@keyframes try-orbit-two {
  from {
    transform: rotate(120deg) translateX(38px) rotate(-120deg);
  }
  to {
    transform: rotate(480deg) translateX(38px) rotate(-480deg);
  }
}

@keyframes try-orbit-three {
  from {
    transform: rotate(240deg) translateX(32px) rotate(-240deg);
  }
  to {
    transform: rotate(600deg) translateX(32px) rotate(-600deg);
  }
}

@keyframes try-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes try-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}

@media (max-width: 640px) {
  .try-download-animation-inner {
    padding: 6px 8px 2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner-aura,
  .spinner-orbit,
  .spinner-ring--outer,
  .spinner-ring--inner {
    animation: none;
  }

  .spinner-orbit {
    display: none;
  }
}
</style>
